import { useEffect, useRef, useSyncExternalStore } from 'react'
import { useLocation, useRouter } from '@tanstack/react-router'
import { JAM_SCRIPT_URLS, JAM_TEAM_ID, type OrganizationFeatures } from '@hub/contracts'
import { i18n } from '@/i18n'
import { CURRENT_BUILD_ID, CURRENT_VERSION } from '@/lib/app-version'
import { useAuthState } from '@/lib/auth-state'
import { useProject } from '@/lib/project-context'
import { useTheme } from '@/lib/theme'
import { installClientEventCapture, recentClientEvents } from './client-events'
import {
  buildJamMetadata,
  fitJamMetadata,
  JAM_EVENTS_IN_METADATA,
  type JamMetadata,
} from './jam-metadata'

/**
 * Jam (jam.dev) bug reports behind the global `jam` setting (super-admin ›
 * Tools, on by default, `worker/db/app-settings.ts`).
 *
 * Jam wants its two module scripts and `<meta name="jam:team">` in the head
 * as early as possible, but the SPA only learns the setting once `me.get`
 * resolves — so the root mounts `<JamLoader>`, which injects them the moment
 * it is known to be on. Never on `/auth`. Scripts are never removed again:
 * turning the setting off takes effect on the next page load.
 *
 * Metadata: the recorder reads `window.__jam__.exports.metadata` when a user
 * creates a Jam (that is all `@jam.dev/sdk` does, so no dependency). The
 * callback is registered before the scripts load and reads live state — the
 * router, `me`, the project layout's scope — at capture time.
 */

/** The project the user is in, published by the project layout (metadata only). */
export interface JamProjectScope {
  organizationId: string
  projectRole: JamMetadata['projectRole']
  features: OrganizationFeatures
}

let projectScope: JamProjectScope | null = null
const scopeListeners = new Set<() => void>()

function setProjectScope(next: JamProjectScope | null): void {
  projectScope = next
  for (const listener of scopeListeners) listener()
}

function subscribeProjectScope(listener: () => void): () => void {
  scopeListeners.add(listener)
  return () => scopeListeners.delete(listener)
}

/** Rendered inside `ProjectProvider`: organization, role and flags for the metadata. */
export function JamProjectScopeSync() {
  const { project } = useProject()
  useEffect(() => {
    setProjectScope({
      organizationId: project.organizationId,
      projectRole: project.role,
      features: project.features,
    })
    return () => setProjectScope(null)
  }, [project.organizationId, project.role, project.features])
  return null
}

let injected = false

/** Add the meta tag and both scripts once (the meta must precede the scripts). */
export function injectJamScripts(doc: Document = document): void {
  if (injected) return
  injected = true
  if (!doc.head.querySelector('meta[name="jam:team"]')) {
    const meta = doc.createElement('meta')
    meta.name = 'jam:team'
    meta.content = JAM_TEAM_ID
    doc.head.prepend(meta)
  }
  for (const src of JAM_SCRIPT_URLS) {
    if (doc.head.querySelector(`script[src="${src}"]`)) continue
    const script = doc.createElement('script')
    script.type = 'module'
    script.src = src
    doc.head.appendChild(script)
  }
}

type JamWindow = Window & { __jam__?: { exports?: { metadata?: () => JamMetadata } } }

function registerMetadata(callback: () => JamMetadata): void {
  const w = window as JamWindow
  const jam = (w.__jam__ ??= {})
  const exports = (jam.exports ??= {})
  exports.metadata = callback
}

export function JamLoader() {
  const { me } = useAuthState()
  const { pathname } = useLocation()
  const router = useRouter()
  const { resolved: theme } = useTheme()
  const scope = useSyncExternalStore(subscribeProjectScope, () => projectScope)

  const enabled = me?.appSettings.jam === true && !pathname.startsWith('/auth')

  // What the metadata callback reads — kept current, evaluated at capture time.
  const live = useRef({ me, scope, theme })
  live.current = { me, scope, theme }

  useEffect(() => {
    if (!enabled) return
    installClientEventCapture()
    registerMetadata(() => {
      const { me, scope, theme } = live.current
      const matches = router.state.matches
      const params: Record<string, string | undefined> = {}
      for (const match of matches) Object.assign(params, match.params)
      return fitJamMetadata(
        buildJamMetadata({
          appVersion: CURRENT_VERSION,
          buildId: CURRENT_BUILD_ID,
          routeId: matches[matches.length - 1]?.routeId ?? '/',
          params,
          organizationId: scope?.organizationId ?? me?.organization?.id,
          projectRole: scope?.projectRole,
          features: scope?.features,
          uiLanguage: i18n.language,
          theme,
          events: recentClientEvents(JAM_EVENTS_IN_METADATA),
        })
      )
    })
    injectJamScripts()
  }, [enabled, router])

  return null
}
